import React from 'react';
import { FormSelect } from '@getro/rombo';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormikAutoSubmit } from '../../../../../atoms/formikAutoSubmit';
import { useCompanyFundingStageFilter } from './useCompanyFundingStageFilter';

const StyledDisclaimer = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.subtle};
  font-style: italic;
`;

export const CompanyFundingStageFilter = ({
  value,
  companyStages,
  stageTransitions,
  activePositionsOrganizationStages,
  setFilters,
}) => {
  // Combine all filter values for the hook
  const filters = {
    companyStages: companyStages || value || [],
    stageTransitions: stageTransitions || [],
    activePositionsOrganizationStages: activePositionsOrganizationStages || [],
  };

  const {
    formSchema,
    onSubmit,
    fundingStageTypeOptions,
    yearsOptions,
    fundOptions,
    onFundingStageTypeChange,
    onStageFromChange,
    setYears,
    setStageTo,
    getAvailableStageToOptions,
    getAvailableStageFromOptions,
    initialValues,
  } = useCompanyFundingStageFilter({
    setFilters,
    filters,
  });

  return (
    <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={onSubmit} enableReinitialize>
      {({ setFieldValue, values }) => (
        <Form>
          <FormikAutoSubmit />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Field
              name="fundingStageType"
              label="Company funding stage"
              placeholder="Select"
              size="small"
              component={FormSelect}
              options={fundingStageTypeOptions}
              anchorToBody
              isSearchable={false}
              onChange={(option) => {
                const resetValues = onFundingStageTypeChange(option);
                setFieldValue('fundingStageType', option);
                // Reset all dependent fields in Formik
                setFieldValue('years', resetValues.years);
                setFieldValue('stageFrom', resetValues.stageFrom);
                setFieldValue('stageTo', resetValues.stageTo);
              }}
            />

            {values.fundingStageType?.value === 'early' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 10 }}>
                  <Field
                    name="years"
                    placeholder="2 years"
                    size="small"
                    component={FormSelect}
                    options={yearsOptions}
                    anchorToBody
                    isSearchable={false}
                    onChange={(option) => {
                      setYears(option);
                      setFieldValue('years', option);
                    }}
                  />
                </div>
                <span style={{ flex: 1, fontSize: '14px' }}>in</span>
                <div style={{ flex: 10 }}>
                  <Field
                    name="stageFrom"
                    placeholder="Seed"
                    size="small"
                    component={FormSelect}
                    options={fundOptions}
                    anchorToBody
                    isSearchable={false}
                    onChange={(option) => {
                      onStageFromChange(option, values.fundingStageType, values.stageTo);
                      setFieldValue('stageFrom', option);
                    }}
                  />
                </div>
              </div>
            )}

            {values.fundingStageType?.value === 'growth' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 10 }}>
                  <Field
                    name="stageFrom"
                    placeholder="Seed"
                    size="small"
                    component={FormSelect}
                    options={getAvailableStageFromOptions(values.fundingStageType)}
                    anchorToBody
                    isSearchable={false}
                    onChange={(option) => {
                      const adjustedStageTo = onStageFromChange(option, values.fundingStageType, values.stageTo);
                      setFieldValue('stageFrom', option);
                      // If stageTo was auto-adjusted, update Formik too
                      if (adjustedStageTo) {
                        setFieldValue('stageTo', adjustedStageTo);
                      }
                    }}
                  />
                </div>
                <span style={{ flex: 1, fontSize: '14px' }}>to</span>
                <div style={{ flex: 10 }}>
                  <Field
                    name="stageTo"
                    placeholder="Series B"
                    size="small"
                    component={FormSelect}
                    options={getAvailableStageToOptions(values.fundingStageType, values.stageFrom)}
                    anchorToBody
                    isSearchable={false}
                    onChange={(option) => {
                      setStageTo(option);
                      setFieldValue('stageTo', option);
                    }}
                  />
                </div>
              </div>
            )}

            {values.fundingStageType?.value === 'current' && (
              <Field
                name="stageFrom"
                placeholder="Seed"
                size="small"
                component={FormSelect}
                options={fundOptions}
                anchorToBody
                isSearchable={false}
                onChange={(option) => {
                  onStageFromChange(option, values.fundingStageType, values.stageTo);
                  setFieldValue('stageFrom', option);
                }}
              />
            )}
          </div>
          {values.fundingStageType?.value && (
            <StyledDisclaimer>Not all companies are covered in our funding stage data.</StyledDisclaimer>
          )}
        </Form>
      )}
    </Formik>
  );
};

CompanyFundingStageFilter.propTypes = {
  value: PropTypes.array,
  companyStages: PropTypes.array,
  stageTransitions: PropTypes.array,
  activePositionsOrganizationStages: PropTypes.array,
  setFilters: PropTypes.func.isRequired,
};

CompanyFundingStageFilter.defaultProps = {
  value: [],
  companyStages: undefined,
  stageTransitions: undefined,
  activePositionsOrganizationStages: undefined,
};
